import { Box, Button, Grid, Typography } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import { ComponentType, FC, ReactElement, useEffect } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { FormProps } from '@utils/types/form';

interface BasicFormProps<T = Record<string, unknown>> {
    Fields: FC<FormProps> | ComponentType;
    cancelLabel?: string;
    disabled?: boolean;
    errorMessage?: string;
    hideCancel?: boolean;
    hideSubmit?: boolean;
    initialValues?: T;
    name?: string;
    onCancel?: () => void;
    onSubmit: SubmitHandler<FieldValues>;
    props?: Record<string, unknown>;
    submitLabel?: string;
}

const BaseForm = <T,>({
    Fields,
    cancelLabel,
    disabled,
    errorMessage,
    hideCancel = true,
    hideSubmit = false,
    initialValues,
    name,
    onCancel,
    onSubmit,
    props = {},
    submitLabel,
}: BasicFormProps<T>): ReactElement => {
    const { clearErrors, control, handleSubmit, register, reset, setValue, watch, formState } =
        useForm<Record<string, unknown>>({
            defaultValues: initialValues || {},
            shouldUnregister: false,
        });
    const { t } = useTranslation('common');

    useEffect(() => {
        // Update RHF initial values if they change
        reset(initialValues as Record<string, unknown>);
    }, [initialValues, reset]);

    const handleCancel = () => {
        if (onCancel) onCancel();
        reset();
    };

    return (
        <form id={name} onSubmit={handleSubmit(onSubmit as SubmitHandler<Record<string, unknown>>)}>
            <Grid direction="column" container>
                <Fields
                    clearErrors={clearErrors}
                    control={control}
                    formDisabled={disabled}
                    formState={formState}
                    register={register}
                    setValue={setValue}
                    watch={watch}
                    {...props}
                />
                {errorMessage && <Typography color="error">{errorMessage}</Typography>}
                {!(hideCancel && hideSubmit) && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        {!hideCancel && (
                            <Button
                                data-test-id="cancel"
                                onClick={handleCancel}
                                variant="contained"
                            >
                                {cancelLabel || t('form.cancel')}
                            </Button>
                        )}
                        {!hideSubmit && (
                            <Button
                                data-test-id="submit"
                                disabled={
                                    Boolean(Object.keys(formState.errors).length) ||
                                    !formState.isDirty ||
                                    disabled
                                }
                                style={{ marginLeft: 20 }}
                                type="submit"
                                variant="contained"
                            >
                                {submitLabel || t('form.submit')}
                            </Button>
                        )}
                    </Box>
                )}
            </Grid>
        </form>
    );
};

export default BaseForm;
